import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "./api";

/** A status as a coloured dot and sentence-case text ("Active"); the colour comes from `.st-<status>`. */
export function StatusBadge({ status }: { status: string }) {
  const text = status.replace(/_/g, " ");
  return (
    <span className={`status st-${status}`}>
      <i aria-hidden="true" />
      {text.charAt(0).toUpperCase() + text.slice(1)}
    </span>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const e = error instanceof ApiError ? error : undefined;
  return (
    <div className="error" role="alert">
      <strong>{e ? `${e.status} ${e.code}` : "Error"}:</strong> {error instanceof Error ? error.message : String(error)}
      {e && e.fields.length > 0 && (
        <ul>
          {e.fields.map((f) => (
            <li key={f.path + f.message}>
              <code>{f.path || "(request)"}</code> {f.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A section of a page: a card, or with `plain` a heading over content that carries its own frame (a list table). */
export function Panel({ title, actions, children, plain }: { title?: string; actions?: ReactNode; children: ReactNode; plain?: boolean }) {
  return (
    <section className={plain ? "panel plain" : "panel"}>
      {(title || actions) && (
        <header>
          {title && <h2>{title}</h2>}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

export function when(d: Date | null | undefined): string {
  return d ? d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—";
}

/** "5 min ago", "yesterday", "in 3 days"; a plain date beyond a week. */
export function relative(d: Date, now = Date.now()): string {
  const s = Math.round((d.getTime() - now) / 1000);
  const a = Math.abs(s);
  const ago = (n: number, unit: string) => (s < 0 ? `${n} ${unit} ago` : `in ${n} ${unit}`);
  // A few minutes "in the future" is clock skew between the server and this browser, not a future time.
  if (a < 45 || (s > 0 && a < 300)) return "just now";
  if (a < 3600) return ago(Math.max(1, Math.round(a / 60)), "min");
  if (a < 86400) return ago(Math.round(a / 3600), "h");
  const days = Math.round(a / 86400);
  if (days === 1) return s < 0 ? "yesterday" : "tomorrow";
  if (days < 7) return ago(days, "days");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** A time for lists and timelines: relative, never wrapping, with the full date and time on hover. */
export function When({ at, empty = "—" }: { at: Date | null | undefined; empty?: string }) {
  if (!at) return <span className="muted">{empty}</span>;
  return (
    <time className="when" dateTime={at.toISOString()} title={when(at)}>
      {relative(at)}
    </time>
  );
}

/** Page title on the left, the page's main action on the right, at most one line of description. */
export function PageHeader({ title, description, actions, back }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <header className="page-head">
      {back && <div className="page-back">{back}</div>}
      <div className="page-head-row">
        <h1>{title}</h1>
        {actions && <div className="page-actions">{actions}</div>}
      </div>
      {description && <p className="lede">{description}</p>}
    </header>
  );
}

/**
 * A form that slides in from the right, for creating things without pushing the list down. A native
 * modal dialog, so focus stays inside and Esc closes it. Submitting calls `onSubmit`; the caller closes
 * it on success.
 */
export function SidePanel({ title, open, onClose, onSubmit, submitLabel, busy, children, error }: {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel: string;
  busy?: boolean;
  error?: unknown;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="side-panel" aria-label={title} onCancel={(e) => (e.preventDefault(), onClose())}>
      {open && (
        <form onSubmit={(e: FormEvent) => (e.preventDefault(), onSubmit())} autoComplete="off">
          <header>
            <h2>{title}</h2>
            <button type="button" className="link close" aria-label="Close" onClick={onClose}>✕</button>
          </header>
          <div className="side-body">
            {children}
            <ErrorBox error={error} />
          </div>
          <footer>
            <button type="button" className="secondary" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={busy}>{submitLabel}</button>
          </footer>
        </form>
      )}
    </dialog>
  );
}

/** A labelled form control with optional help text underneath. */
export function Field({ label, help, children }: { label: string; help?: ReactNode; children: ReactNode }) {
  return (
    <div className="field">
      <label>
        <span className="field-label">{label}</span>
        {children}
      </label>
      {help && <small className="field-help">{help}</small>}
    </div>
  );
}

export interface MenuItem {
  label: string;
  onSelect: () => void;
  /** destructive: shown last, in red */
  danger?: boolean;
}

/**
 * A row's actions behind one ⋯ button. Destructive items go last and red. The menu is placed against
 * the viewport, so a table that scrolls sideways cannot clip it; Esc, a click outside or scrolling close it.
 */
export function RowMenu({ label, items }: { label: string; items: (MenuItem | false | null | undefined)[] }) {
  const list = items.filter((i): i is MenuItem => !!i).sort((a, b) => Number(!!a.danger) - Number(!!b.danger));
  const [pos, setPos] = useState<{ top: number; right: number }>();
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pos) return;
    menu.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const close = () => setPos(undefined);
    const onDown = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") (close(), btn.current?.focus());
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const all = [...(menu.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
        const i = all.indexOf(document.activeElement as HTMLButtonElement);
        all[(i + (e.key === "ArrowDown" ? 1 : all.length - 1)) % all.length]?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [pos]);
  if (list.length === 0) return null;
  const toggle = () => {
    const r = btn.current?.getBoundingClientRect();
    setPos(pos || !r ? undefined : { top: r.bottom + 4, right: window.innerWidth - r.right });
  };
  return (
    <>
      <button ref={btn} type="button" className="menu-btn" aria-label={label} aria-haspopup="menu" aria-expanded={!!pos} onClick={toggle}>
        <span aria-hidden="true">⋯</span>
      </button>
      {pos && (
        <div ref={menu} role="menu" aria-label={label} className="menu" style={{ top: pos.top, right: pos.right }}>
          {list.map((i, n) => (
            <button key={i.label} type="button" role="menuitem" className={`${i.danger ? "danger" : ""}${i.danger && n > 0 && !list[n - 1]?.danger ? " sep" : ""}`} onClick={() => (setPos(undefined), i.onSelect())}>
              {i.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** The workcore mark: nested squares, echoing containers holding objects holding objects. */
export function BrandMark({ size = 20 }: { size?: number }) {
  return (
    <svg className="mark" width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <rect x="1" y="1" width="18" height="18" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="8.5" y="8.5" width="3" height="3" rx="0.5" fill="currentColor" />
    </svg>
  );
}

/** Where this page sits: Workspace › Section › Item. The last crumb is the page itself and is not a link. */
export function Breadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      <ol>
        {items.map((c, i) => (
          <li key={`${i}-${c.label}`}>{c.to && i < items.length - 1 ? <Link to={c.to}>{c.label}</Link> : <span aria-current={i === items.length - 1 ? "page" : undefined}>{c.label}</span>}</li>
        ))}
      </ol>
    </nav>
  );
}

let pushToast: ((text: string) => void) | undefined;
/** A short notice in the corner ("Saved"), gone after a couple of seconds. */
export function toast(text: string): void {
  pushToast?.(text);
}
/** Mounted once, in the shell. Announced politely to screen readers. */
export function ToastHost() {
  const [items, setItems] = useState<{ id: number; text: string }[]>([]);
  useEffect(() => {
    let n = 0;
    pushToast = (text) => {
      const id = ++n;
      setItems((xs) => [...xs.slice(-2), { id, text }]);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 2200);
    };
    return () => {
      pushToast = undefined;
    };
  }, []);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((t) => <div key={t.id} className="toast">{t.text}</div>)}
    </div>
  );
}

/** What a list is for and how to start one, in place of an empty table. */
export function EmptyState({ title, body, action }: { title: string; body: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      <p>{body}</p>
      {action}
    </div>
  );
}

/**
 * Shown after an invitation is made. workcore sends no email: the person joins by signing in to the
 * console with the invited address, so the inviter has to tell them where.
 */
export function InviteSent({ email, onDone }: { email: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = `You've been invited to workcore. Sign in at ${window.location.origin} with ${email}.`;
  return (
    <div className="notice" role="status">
      <strong>Invitation saved for {email}.</strong>
      <p>workcore doesn't send email. Let them know to sign in at <code>{window.location.origin}</code> with that address; they join on first sign-in.</p>
      <div className="row">
        <button type="button" onClick={() => void navigator.clipboard.writeText(text).then(() => setCopied(true))}>{copied ? "Copied" : "Copy a message for them"}</button>
        <button type="button" className="secondary" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}
